import { ReactElement, useMemo } from 'react'
import { hashObject } from '@scene'
import { RoofSlabForm, WallForm } from '@structuralPlanningComponents'
import { Typography } from '@mui/material'
import { CorneredSection } from '@ui2/structure'
import { useEditElementStore, useModelStore } from '@editorStores'
import { useElementType, useIfcMode } from '@editorHooks'
import { wallTabs } from '../../../StructuralPlanning/components/WallForm/tabs'
import useIFCImportGuided from '../../hooks/useIFCImportGuided'
import IFCWallMultiEdit from '../IFCWallMultiEdit'

interface Props {
  /** Hint shown while no planar element is selected. */
  placeholder?: string
}

/**
 * Renders the Structural Planning edit form (Bauteil tab only) for the currently active planar
 * element, dispatching by its type. Shared by the IFC planar edit drawer and the guided edit steps.
 * A wall multi selection (shift-click/marquee) takes precedence and shows the importer's wall
 * multi-edit instead.
 */
const IFCPlanarElementForm = ({
  placeholder = 'Wählen Sie ein Element im Modell aus, um es zu bearbeiten.',
}: Props): ReactElement => {
  const activeElement = useEditElementStore(state => state.activeElement)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const multiActiveElements = useEditElementStore(state => state.multiActiveElements)

  const elementType = useElementType(activeElement)

  const { setIfcMode } = useIfcMode()
  const { isGuidedActive } = useIFCImportGuided()

  // "in Draufsicht bearbeiten" (inside the wall's "Öffnungen" section) switches the importer's own
  // `draw-openings` ifcMode instead of Structural Planning's, which the importer doesn't react to.
  // Omitted in the guided flow, where switching ifcMode would silently exit it (ifcMode holds
  // exactly one value at a time, and the guided wall-edit step has no top-view variant of its own).
  const onEditOpeningInTopView = isGuidedActive
    ? undefined
    : (wallGuid: string) => {
        setActiveElement(wallGuid)
        setIfcMode('draw-openings')
      }

  const selectedElements = useMemo(() => (activeElement ? [activeElement] : []), [activeElement])

  // Key the wall form by the wall's contents so it reinitialises when the geometry changes in the
  // scene (e.g. moving/extending via WallEdit), instead of keeping the stale coordinates it was
  // mounted with (which a save would then write back). Scene edits auto-save (see
  // `useIFCImportWallEditAutoSave`), so no revert-on-deselect history is needed here.
  const walls = useModelStore(state => state.model.walls)
  const activeWall = useMemo(
    () => walls.find(wall => wall.guid === activeElement),
    [walls, activeElement],
  )
  const wallFormKey = activeWall ? hashObject(activeWall) : activeElement

  if (multiActiveElements.length > 0) return <IFCWallMultiEdit />

  if (!activeElement)
    return (
      <CorneredSection>
        <Typography variant="body2" color="text.secondary">
          {placeholder}
        </Typography>
      </CorneredSection>
    )

  if (elementType === 'outer_walls' || elementType === 'inner_walls')
    return (
      <CorneredSection>
        <WallForm
          key={wallFormKey}
          elementType={elementType}
          selectedElementGuid={activeElement}
          onClose={() => setActiveElement(null)}
          initialTabValue={wallTabs.geometry.tab}
          hideTabs
          mode="architectural"
          onEditOpeningInTopView={onEditOpeningInTopView}
        />
      </CorneredSection>
    )

  if (elementType === 'slabs' || elementType === 'roof_slabs')
    return (
      <CorneredSection>
        <RoofSlabForm
          key={activeElement}
          elementType={elementType}
          selectedElements={selectedElements}
          selectElement={guid => setActiveElement(guid)}
          onClose={() => setActiveElement(null)}
          hideTabs
          mode="architectural"
        />
      </CorneredSection>
    )

  return (
    <CorneredSection>
      <Typography variant="body2" color="text.secondary">
        Dieses Element kann nicht bearbeitet werden.
      </Typography>
    </CorneredSection>
  )
}

export default IFCPlanarElementForm
